import type {
  DataGridColumnSettings,
  DataGridDataRowTemplateProps,
  DataGridRef,
  DataGridRowIndicator,
  DataGridSettings,
  DropDownResult,
  DynamicFormFieldType,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  RowTemplateType,
} from '@myosh/odin-components';
import { DataGrid, Link, Notification, OdinDataSender } from '@myosh/odin-components';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchLinkedRecordColumnValues } from '../../../../hooks/use-fetch-linked-record-column-values';
import useGridSettings from '../../../../hooks/use-grid-settings';
import useRenderDataGrid from '../../../../hooks/use-render-data-grid';
import useFetchWorkflowSteps from '../../../../hooks/use-workflow-steps';
import { useAppSelector } from '../../../../redux/hooks';
import { useFormSettingsQuery } from '../../../../redux/services/api';
import { useGetRecordCountQuery, useGetRecordsQuery } from '../../../../redux/services/record';
import { selectedGlobalHierarchies, selectedMatchingOption } from '../../../../redux/slices/global-hierarchies';
import { getDataGridComboboxFilters } from '../../../../utils/data-grid-filters';
import { dateFormatBasedOnFilter } from '../../../../utils/data-transformation-utils';
import {
  getCurrentlyLoadedAndTotalRecords,
  processLastEditedFilterForContinousScroller,
  processRecordFilterForContinuousScroller,
} from '../../../../utils/dynamic-page-utils';
import { buildGlobalHierarchyFiltersUrl, processFieldFilters } from '../../../../utils/filter-url-builder-utils';
import { buildSortedFieldsUrlString } from '../../../../utils/sort-url-builder-utils';
import { DataGridLoader } from '../../../dynamic-page/data-grid-loader.component';

const mapHighlightedRows = (highlightedRows: Array<string>) =>
  highlightedRows?.map((id) => {
    return { id: Number(id) };
  });

interface AllRecordsGridProps {
  recordId: number | undefined;
  formId?: number;
  configId?: number;
  defaultGridSettings: DataGridSettings;
  columnConfigurator: (configColumn: DataGridColumnSettings) => DataGridColumnSettings;
  rowIndicator: DataGridRowIndicator;
  onAdded: (id: string) => void;
  highlightedRows: Array<string>;
  selectedRecordsFilter: DropDownResult;
  selectedLastEditedFilter: DropDownResult;
  previewRecordId?: string;
}

// Displays all records that can be linked to the field
export default function AllRecordsGrid({
  recordId, // the recordId of the record where the linked records field is located
  formId,
  configId,
  defaultGridSettings,
  columnConfigurator,
  rowIndicator,
  onAdded,
  highlightedRows,
  previewRecordId,
  selectedLastEditedFilter,
  selectedRecordsFilter,
}: AllRecordsGridProps) {
  const [allRecordsGridSetting, setAllRecordsGridSetting] = useState<DataGridSettings>();

  const { t } = useTranslation();
  const gridRef = useRef<DataGridRef>(null);
  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();
  const globalFilters = useAppSelector(selectedGlobalHierarchies);
  const globalFiltersMatchingOption = useAppSelector(selectedMatchingOption);

  const {
    settings: gridSettings,
    error: gridSettingsError,
    loading: gridSettingsLoading,
  } = useGridSettings(defaultGridSettings, columnConfigurator, formId, configId);

  const { data: formSettings } = useFormSettingsQuery(formId ?? 0, {
    skip: formId === undefined || formId <= 0,
  });

  const { workflowSteps, isFetchingWorkflowSteps } = useFetchWorkflowSteps(formId!);

  const { fetchLinkedRecordColumnValues, cancelLinkedRecordColumnValuesPendingRequests } =
    useFetchLinkedRecordColumnValues(formId ?? 0);

  const {
    data: recordsData,
    isLoading: isRecordsLoading,
    isFetching: isRecordsFetching,
  } = useGetRecordsQuery(
    {
      keyTypeId: true,
      formId: formId || 0,
      filters: processFieldFilters(gridOptions?.fieldFilters, dateFormatBasedOnFilter),
      globalHierarchyFilters: buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption),
      sortDirective: buildSortedFieldsUrlString(gridOptions?.sortedFields, gridRef?.current?.api.data.columns),
      page: gridOptions?.page,
      pageSize: gridOptions?.pageSize,
      excludedRecordId: recordId,
    },
    {
      skip:
        formId === undefined ||
        formId <= 0 ||
        gridOptions === undefined ||
        gridOptions?.page === undefined ||
        gridOptions?.pageSize === undefined ||
        gridSettingsLoading,
    }
  );

  const isRecordsLoaded: boolean = !isRecordsLoading && !isRecordsFetching;

  const {
    data: recordCount,
    isLoading: isRecordsCountLoading,
    isFetching: isRecordsCountFetching,
  } = useGetRecordCountQuery(
    {
      formId: formId || 0,
      filters: processFieldFilters(gridOptions?.fieldFilters, dateFormatBasedOnFilter),
      excludedRecordId: recordId,
    },
    {
      skip: formId === undefined || formId === 0 || gridOptions === undefined || gridSettingsLoading,
    }
  );

  const isRecordsCountLoaded: boolean = !isRecordsCountLoading && !isRecordsCountFetching;

  const gridData = useMemo<OdinDataRetrieval | undefined>(() => {
    if (!isFetchingWorkflowSteps)
      return {
        getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
          if (fieldType !== 'COMBOBOX') {
            gridSubscriber.current = subscriber;
          }
        },
        getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
          if (options?.fieldType === 'COMBOBOX') {
            const selectedForm: DropDownResult = {
              value: formId,
              text: formSettings?.caption as string,
            };
            await getDataGridComboboxFilters(
              subscriber,
              options,
              false,
              {
                selectedForm,
                workflowSteps,
              },
              formSettings
            );
          } else {
            setGridOptions(options);
          }
        },
      };
  }, [workflowSteps, isFetchingWorkflowSteps]);

  const canRenderDataGrid =
    !gridSettingsLoading && !!gridSettings?.columns && !!gridData && !!allRecordsGridSetting?.columns;

  const { renderDataGrid } = useRenderDataGrid(canRenderDataGrid);

  useEffect(() => {
    return () => {
      cancelLinkedRecordColumnValuesPendingRequests();
    };
  }, [cancelLinkedRecordColumnValuesPendingRequests]);

  useEffect(() => {
    if (gridRef.current && canRenderDataGrid) {
      gridRef.current.api.filters.addFilter(processRecordFilterForContinuousScroller(selectedRecordsFilter));
    }
  }, [selectedRecordsFilter, canRenderDataGrid]);

  useEffect(() => {
    if (gridRef.current && canRenderDataGrid) {
      gridRef.current.api.filters.addFilter(processLastEditedFilterForContinousScroller(selectedLastEditedFilter));
    }
  }, [selectedLastEditedFilter, canRenderDataGrid]);

  useEffect(() => {
    if (!recordsData || !isRecordsLoaded || !gridSettings || gridSettingsLoading) {
      return;
    }

    const data = recordsData.map((record) => record.flat);

    if (recordsData.length === 0) {
      gridSubscriber.current?.sendData();
      return;
    }

    // Fetch linked records and inject into row data before sending to grid
    fetchLinkedRecordColumnValues(gridSettings.columns, data).then((linkedRecordsMap) => {
      const dataWithLinkedRecords = data.map((row) => ({
        ...row,
        _linkedRecordColumnValuesMap: linkedRecordsMap,
      }));

      gridSubscriber.current?.sendData({
        data: dataWithLinkedRecords,
        requestId: gridOptions?.requestId,
      });
    });
  }, [recordsData, isRecordsLoaded, gridOptions, gridSettingsLoading, gridSettings, fetchLinkedRecordColumnValues]);

  useEffect(() => {
    if (canRenderDataGrid && gridRef.current) {
      if (highlightedRows.length > 0) {
        gridRef.current.api.data.addHighlightedRows(mapHighlightedRows(highlightedRows), true);
      } else {
        gridRef.current.api.data.removeHighlightedRows();
      }
    }
  }, [highlightedRows, canRenderDataGrid]);

  useEffect(() => {
    if (!previewRecordId) {
      gridRef.current?.api.data.removeSelectedRows();
    }
  }, [previewRecordId]);

  useEffect(() => {
    if (!gridSettingsLoading && gridSettings?.columns) {
      setAllRecordsGridSetting({
        ...gridSettings,
        headerRowIndicator,
      });
    }
  }, [gridSettings, gridSettingsLoading]);

  const headerRowIndicator = {
    indicator: <Link classNames="text-xs">{t('add-all')}</Link>,
    visible: false,
  };

  const recordCountDisplayValue = useMemo<string | undefined>(() => {
    if (isRecordsLoaded && isRecordsCountLoaded && recordsData && !gridSettingsLoading) {
      const currentPageRecordsCount = recordsData.length;

      return getCurrentlyLoadedAndTotalRecords(
        currentPageRecordsCount,
        recordCount ?? 0,
        gridOptions?.pageSize ?? 1,
        gridOptions?.page ?? 1
      );
    }
    return undefined;
  }, [isRecordsLoaded, isRecordsCountLoaded, recordsData, gridSettingsLoading]);

  const addLinkedRecord = (id: string) => onAdded(id);

  const selectingRowTemplate: RowTemplateType = (props: DataGridDataRowTemplateProps, children: React.ReactNode) => (
    <div className="flex" onClick={() => addLinkedRecord((props.data['id'] as number).toString())}>
      {children}
    </div>
  );

  const containerStyles = clsx('flex h-full flex-col', { 'justify-center': gridSettingsError });

  return (
    <div className={containerStyles}>
      {!renderDataGrid && !gridSettingsError && <DataGridLoader />}
      {canRenderDataGrid && (
        <>
          <DataGrid
            ref={gridRef}
            data={gridData}
            gridSettings={allRecordsGridSetting}
            rowIndicator={rowIndicator}
            rowTemplate={selectingRowTemplate}
            showGridMenu={false}
          />
          <div className="mb-5 px-10 font-semibold">{recordCountDisplayValue}</div>
        </>
      )}
      {gridSettingsError && (
        <div className="flex justify-center">
          <Notification
            key="configNotification"
            title={t('error-config-title')}
            description={t('error-config-view-description')}
            type="error"
          />
        </div>
      )}
    </div>
  );
}
